
import React, { useEffect, useRef, useState } from 'react';
import { Menu, X, Eye, Volume2, LogIn, LayoutDashboard, ChevronDown } from 'lucide-react';
import { Link, useLocation } from 'react-router-dom';
import { useAccessibility } from '../hooks/useAccessibility';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { recursos } from '../data/recursos';
import logoBranca from '../assets/logo_branca.png';
import logoAzul from '../assets/logo_azul.webp';

/** variant="reading": só a tela do artigo do blog usa — troca o fundo/borda
 *  padrão do site pelo tom "papel" da zona de leitura (ver .reading-nav em
 *  index.css), pra nav não cortar o fundo contínuo com uma faixa branca/escura. */
const Header = ({ variant = 'default' }: { variant?: 'default' | 'reading' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isAccessibilityOpen, setIsAccessibilityOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isRecursosOpen, setIsRecursosOpen] = useState(false);
  const [isRecursosMobileOpen, setIsRecursosMobileOpen] = useState(false);
  const recursosCloseTimer = useRef<ReturnType<typeof setTimeout>>();
  const location = useLocation();
  const {
    isDarkMode,
    isHighContrast,
    isLargeFont,
    isScreenReaderOn,
    toggleHighContrast,
    toggleLargeFont,
    toggleScreenReader,
    speak
  } = useAccessibility();
  const { user } = useAuth();

  // Detect scroll position
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fecha qualquer menu aberto (mobile, dropdown de Recursos) sempre que a
  // rota muda — evita menu aberto "grudado" depois de navegar.
  useEffect(() => {
    setIsMenuOpen(false);
    setIsRecursosOpen(false);
    setIsRecursosMobileOpen(false);
  }, [location.pathname]);

  const openRecursosMenu = () => {
    if (recursosCloseTimer.current) clearTimeout(recursosCloseTimer.current);
    setIsRecursosOpen(true);
  };
  const scheduleCloseRecursosMenu = () => {
    recursosCloseTimer.current = setTimeout(() => setIsRecursosOpen(false), 150);
  };

  // "Recursos" não tem mais página própria — vira um botão que abre um
  // dropdown com a lista de recursos, cada um levando à sua própria página.
  const menuItems = [
    { name: 'Início', href: '/' },
    { name: 'Blog', href: '/blog' },
    { name: 'Suporte', href: '/suporte' },
    { name: 'Fale Conosco', href: '/contato' }
  ];

  // Herói atmosférico só existe na Home — enquanto não rolou, a nav fica
  // fixa, transparente e com texto branco sobre o gradiente. Assim que o
  // usuário rola para além do hero, a nav simplesmente some — ela é
  // exclusiva do hero na Home. Nas demais páginas ela não é fixa nem um card
  // flutuante: é uma barra normal, de ponta a ponta, no topo do conteúdo.
  const isHome = location.pathname === '/';
  const isHomeHero = isHome && !isScrolled;
  const isHiddenPastHero = isHome && isScrolled;
  const shouldUseWhiteLogo = isDarkMode || isHomeHero;
  const isRecursosActive = location.pathname.startsWith('/recursos');

  return (
    <header
      className={`inset-x-0 z-50 transition-all duration-300 ${
        isHome
          ? `fixed top-0 flex justify-center px-3 md:px-4 ${
              isHiddenPastHero ? 'opacity-0 -translate-y-4 pointer-events-none' : 'opacity-100 translate-y-0'
            }`
          : variant === 'reading'
            ? 'relative reading-nav backdrop-blur-md border-b'
            : 'relative bg-background/95 backdrop-blur-md border-b border-border/60'
      }`}
    >
      <div className={`w-full max-w-5xl ${isHome ? '' : 'mx-auto'}`}>
        <div className="flex items-center justify-between h-14 md:h-16 px-4 md:px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src={shouldUseWhiteLogo ? logoBranca : logoAzul}
              alt="CONECTAR Logo"
              className="h-9 md:h-10 w-auto object-contain drop-shadow-lg"
            />
            <span className={`font-bold text-lg ${isDarkMode || isHomeHero ? 'text-white' : 'text-primary'}`}>
              Conectar
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-7">
            <Link
              to="/"
              className={`group relative pb-1 font-medium transition-colors duration-200 ${
                isHomeHero
                  ? location.pathname === '/' ? 'text-white' : 'text-white/80 hover:text-white'
                  : location.pathname === '/' ? 'text-primary' : 'text-foreground hover:text-primary'
              }`}
              style={{ fontSize: isLargeFont ? '1rem' : '0.9375rem' }}
              onClick={() => speak('Início')}
            >
              Início
              <span
                className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-current transition-all duration-300 ${
                  location.pathname === '/' ? 'w-full' : 'w-0 group-hover:w-full'
                }`}
                aria-hidden="true"
              />
            </Link>

            {/* Recursos — abre um dropdown com a lista de recursos ao passar o mouse (ou tocar/focar) */}
            <div className="relative" onMouseEnter={openRecursosMenu} onMouseLeave={scheduleCloseRecursosMenu}>
              <button
                type="button"
                className={`group relative flex items-center gap-1 pb-1 font-medium transition-colors duration-200 ${
                  isHomeHero
                    ? isRecursosActive ? 'text-white' : 'text-white/80 hover:text-white'
                    : isRecursosActive ? 'text-primary' : 'text-foreground hover:text-primary'
                }`}
                style={{ fontSize: isLargeFont ? '1rem' : '0.9375rem' }}
                onClick={() => setIsRecursosOpen((v) => !v)}
                aria-haspopup="true"
                aria-expanded={isRecursosOpen}
              >
                Recursos
                <ChevronDown className={`w-3.5 h-3.5 transition-transform duration-200 ${isRecursosOpen ? 'rotate-180' : ''}`} />
                <span
                  className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-current transition-all duration-300 ${
                    isRecursosActive || isRecursosOpen ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                  aria-hidden="true"
                />
              </button>

              <div
                className={`absolute left-1/2 top-full w-64 -translate-x-1/2 pt-3 transition-all duration-200 ${
                  isRecursosOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                }`}
              >
                <div className="rounded-2xl bg-popover border border-border shadow-xl overflow-hidden py-1">
                  {recursos.map((item) => (
                    <Link
                      key={item.slug}
                      to={`/recursos/${item.slug}`}
                      className="group relative block px-4 py-2.5 text-sm font-semibold text-foreground transition-colors hover:text-primary"
                      onClick={() => {
                        setIsRecursosOpen(false);
                        speak(item.label);
                      }}
                    >
                      <span className="relative inline-block pb-1">
                        {item.label}
                        <span className="absolute left-0 -bottom-0.5 h-0.5 w-0 rounded-full bg-current transition-all duration-300 group-hover:w-full" />
                      </span>
                    </Link>
                  ))}
                </div>
              </div>
            </div>

            {menuItems.slice(1).map((item) => {
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`group relative pb-1 font-medium transition-colors duration-200 ${
                    isHomeHero
                      ? isActive ? 'text-white' : 'text-white/80 hover:text-white'
                      : isActive ? 'text-primary' : 'text-foreground hover:text-primary'
                  }`}
                  style={{
                    fontSize: isLargeFont ? '1rem' : '0.9375rem'
                  }}
                  onClick={() => speak(item.name)}
                >
                  {item.name}
                  <span
                    className={`absolute left-0 -bottom-0.5 h-0.5 rounded-full bg-current transition-all duration-300 ${
                      isActive ? 'w-full' : 'w-0 group-hover:w-full'
                    }`}
                    aria-hidden="true"
                  />
                </Link>
              );
            })}
          </nav>

          {/* Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* Entrar/Painel — mesma cor e estilo do botão principal da hero:
                azul do Conectar, cápsula, com leve elevação no hover. */}
            {user ? (
              <Button
                variant="default"
                size="sm"
                asChild
                className="hidden sm:inline-flex rounded-full font-bold hover:-translate-y-0.5 transition-transform"
              >
                <Link to="/dashboard" onClick={() => speak('Painel')}>
                  <LayoutDashboard className="h-4 w-4" />
                  Painel
                </Link>
              </Button>
            ) : (
              <Button
                variant="default"
                size="sm"
                asChild
                className="hidden sm:inline-flex rounded-full font-bold hover:-translate-y-0.5 transition-transform"
              >
                <Link to="/login" onClick={() => speak('Entrar')}>
                  <LogIn className="h-4 w-4" />
                  Entrar
                </Link>
              </Button>
            )}

            {/* Accessibility Menu — segundo (e último) elemento com animação de hover. */}
            <div className="relative">
              <button
                onClick={() => setIsAccessibilityOpen(!isAccessibilityOpen)}
                className={`p-2 rounded-lg transition-all hover:scale-110 ${
                  isHomeHero ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-secondary hover:bg-secondary/80 text-foreground'
                }`}
                aria-label="Opções de acessibilidade"
              >
                <Eye className="h-4 w-4" />
              </button>

              {isAccessibilityOpen && (
                <div className="glass absolute right-0 top-full mt-2 w-56 rounded-lg p-4 z-10">
                  <h3 className="text-sm font-bold mb-3 text-foreground flex items-center gap-2">
                    <Eye className="h-4 w-4 text-primary" />
                    Acessibilidade
                  </h3>
                  <div className="space-y-2">
                    <button
                      onClick={toggleHighContrast}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-secondary text-sm transition-colors"
                    >
                      <span className="font-medium">Alto Contraste</span>
                      {isHighContrast && <span className="text-primary font-bold">✓</span>}
                    </button>

                    <button
                      onClick={toggleLargeFont}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-secondary text-sm transition-colors"
                    >
                      <span className="font-medium">Fonte Grande</span>
                      {isLargeFont && <span className="text-primary font-bold">✓</span>}
                    </button>

                    <button
                      onClick={toggleScreenReader}
                      className="flex items-center justify-between w-full p-2 rounded-lg hover:bg-secondary text-sm transition-colors"
                    >
                      <div className="flex items-center gap-2">
                        <Volume2 className="h-4 w-4 text-primary" />
                        <span className="font-medium">Leitor de Tela</span>
                      </div>
                      {isScreenReaderOn && <span className="text-primary font-bold">✓</span>}
                    </button>

                    {isScreenReaderOn && (
                      <button
                        onClick={() => speak('Leitor de tela ativado. Navegue pelo site usando as teclas Tab e Enter.')}
                        className="flex items-center space-x-2 w-full p-2 rounded-lg bg-primary/10 hover:bg-primary/20 text-primary text-sm font-medium transition-colors mt-2"
                      >
                        <Volume2 className="h-4 w-4" />
                        <span>Testar Leitor</span>
                      </button>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile Menu Button — só alterna cor/fundo, sem animação extra. */}
            <button
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              className={`lg:hidden p-2 rounded-lg transition-colors ${
                isHomeHero ? 'bg-white/15 hover:bg-white/25 text-white' : 'bg-secondary hover:bg-secondary/80 text-foreground'
              }`}
              aria-label="Menu principal"
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <nav
            className={`lg:hidden px-4 md:px-6 pb-4 border-t ${
              isHome ? 'border-border bg-background/95 backdrop-blur-md' : 'border-border/60'
            }`}
          >
            <div className="space-y-2 pt-3">
              <Link
                to="/"
                className={`block px-4 py-3 font-medium rounded-lg transition-colors ${
                  location.pathname === '/'
                    ? 'bg-primary text-primary-foreground'
                    : 'text-foreground hover:text-primary hover:bg-secondary'
                }`}
                style={{ fontSize: isLargeFont ? '1rem' : '0.9375rem' }}
                onClick={() => {
                  setIsMenuOpen(false);
                  speak('Início');
                }}
              >
                Início
              </Link>

              {/* Recursos — acordeão no mobile, já que não dá pra passar o mouse num toque */}
              <div>
                <button
                  type="button"
                  className={`flex items-center justify-between w-full px-4 py-3 font-medium rounded-lg transition-colors ${
                    isRecursosActive
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:text-primary hover:bg-secondary'
                  }`}
                  style={{ fontSize: isLargeFont ? '1rem' : '0.9375rem' }}
                  onClick={() => setIsRecursosMobileOpen((v) => !v)}
                  aria-expanded={isRecursosMobileOpen}
                >
                  Recursos
                  <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${isRecursosMobileOpen ? 'rotate-180' : ''}`} />
                </button>

                {isRecursosMobileOpen && (
                  <div className="ml-4 mt-1 space-y-1 border-l-2 border-border pl-3">
                    {recursos.map((item) => (
                      <Link
                        key={item.slug}
                        to={`/recursos/${item.slug}`}
                        className="block px-3 py-2.5 text-sm font-medium rounded-lg text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
                        onClick={() => {
                          setIsMenuOpen(false);
                          speak(item.label);
                        }}
                      >
                        {item.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>

              {menuItems.slice(1).map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={`block px-4 py-3 font-medium rounded-lg transition-colors ${
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground hover:text-primary hover:bg-secondary'
                    }`}
                    style={{
                      fontSize: isLargeFont ? '1rem' : '0.9375rem'
                    }}
                    onClick={() => {
                      setIsMenuOpen(false);
                      speak(item.name);
                    }}
                  >
                    {item.name}
                  </Link>
                );
              })}
              <Link
                to={user ? '/dashboard' : '/login'}
                className="block px-4 py-3 font-medium rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                onClick={() => {
                  setIsMenuOpen(false);
                  speak(user ? 'Painel' : 'Entrar');
                }}
              >
                {user ? 'Painel' : 'Entrar'}
              </Link>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
